import { useEffect, useRef } from 'react';
import { colorModeOptions, paletteOptions } from '../searchConfig';
import { ColorMode, Palette } from '../types';

type ThemeMenuProps = {
  colorMode: ColorMode;
  palette: Palette;
  showPaletteControls: boolean;
  showThemeMenu: boolean;
  onColorModeChange: (colorMode: ColorMode) => void;
  onPaletteChange: (palette: Palette) => void;
  onToggleThemeMenu: () => void;
};

export function ThemeMenu({
  colorMode,
  palette,
  showPaletteControls,
  showThemeMenu,
  onColorModeChange,
  onPaletteChange,
  onToggleThemeMenu,
}: ThemeMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showThemeMenu) {
      return;
    }

    const closeOnClickAway = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }

      onToggleThemeMenu();
    };

    document.addEventListener('mousedown', closeOnClickAway);

    return () => document.removeEventListener('mousedown', closeOnClickAway);
  }, [onToggleThemeMenu, showThemeMenu]);

  return (
    <div className="theme-menu" ref={menuRef}>
      <button
        className="theme-menu-trigger"
        type="button"
        aria-label="Theme settings"
        aria-expanded={showThemeMenu}
        onClick={onToggleThemeMenu}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3v2" />
          <path d="M12 19v2" />
          <path d="M4.9 4.9 6.3 6.3" />
          <path d="m17.7 17.7 1.4 1.4" />
          <path d="M3 12h2" />
          <path d="M19 12h2" />
          <path d="m4.9 19.1 1.4-1.4" />
          <path d="m17.7 6.3 1.4-1.4" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      </button>

      {showThemeMenu && (
        <div className="theme-menu-panel">
          {showPaletteControls && (
            <fieldset className="theme-palette-options">
              <legend>Theme</legend>
              {paletteOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={palette === option.value}
                  onClick={() => onPaletteChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </fieldset>
          )}
          <fieldset className="theme-mode-options">
            <legend>Mode</legend>
            {colorModeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={colorMode === option.value}
                onClick={() => onColorModeChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </fieldset>
        </div>
      )}
    </div>
  );
}
